/**
 * The 404 — Step 171.
 *
 * It used to be `PlaceholderPage`, the scaffold-era component that said "Not
 * built yet — deliberately", showed a step number and pointed at
 * `CHANGELOG.md`. That was right while the shell was being built and every
 * route needed to render something real-shaped. It was still the 404 on the
 * live install, where it tells a customer that our repository has a build
 * order — which is neither true of their situation nor any of their business.
 *
 * What a 404 owes somebody: say plainly that the address is wrong, do not
 * imply they broke something, and give them somewhere to go. Nothing else.
 */
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Compass, ArrowLeft, LayoutDashboard, LifeBuoy } from 'lucide-react';
import { Button } from '../components/ui/button';

export function NotFoundPage() {
  const navigate = useNavigate();
  // Rendered both as a route element and as an error element; only the latter
  // has an error to read, and a non-404 error should still say something true.
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : 404;
  const isMissing = status === 404;

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-16 text-center">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Compass className="h-5 w-5 text-muted-foreground" aria-hidden />
      </div>
      <h1 className="text-lg font-semibold">
        {isMissing ? 'There is nothing at this address' : 'Something went wrong'}
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        {isMissing
          ? 'The page may have been renamed or removed, or the link that brought you here may be out of date. Nothing is broken.'
          : 'That page could not be loaded. Trying again often works; if it keeps happening, raise a ticket and we will look.'}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        <Button onClick={() => navigate('/')}>
          <LayoutDashboard className="h-4 w-4" />
          Go to the dashboard
        </Button>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button variant="outline" onClick={() => navigate('/catalogue')}>
          <LifeBuoy className="h-4 w-4" />
          Raise a request
        </Button>
      </div>
    </div>
  );
}
